import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";

import { RootState } from "../../../store/Index";
import { ICreateRateDialogProps, IRoleSkillTool } from "../types/rate-card";
import apiService from "../../../service/Api";
import { Close } from "@mui/icons-material";
import CustomTextField from "../../../components/common/Textfield";

export const CreateRateDialog: React.FC<ICreateRateDialogProps> = ({
  formData,
  formLoading,
  handleCreateRate,
  handleInputChange,
  handleRolesChange,
  open,
  resetForm,
  selectedRoles,
  setDialogOpen,
  setSelectedRoles,
  setRateType,
}) => {
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [roleVal, setRoleVal] = useState<string | null>("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: IRoleSkillTool[] }>(
        "/api/crews/roles/",
        { page_size: 20 }
      );

      const mapped = roles.data.results.map(rl => (
        `${rl.id} - ${rl.name}`
      ));

      setRoleOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setDialogOpen(false);
        resetForm();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Add New Rate Card
        <IconButton
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("");
            resetForm();
          }}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={formLoading}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              value={roleVal}
              onChange={(event: React.SyntheticEvent, newValue: string | null) => {
                setRoleVal(newValue);
              }}
              loading={rolesLoading}
              inputValue={selectedRoles}
              onInputChange={(event, newInputValue) => {
                handleRolesChange(newInputValue);
              }}
              options={roleOptions}
              sx={{ width: '100%', borderRadius: 2 }}
              renderInput={(params) => 
                <CustomTextField
                  {...params}
                  placeholder="Select role..."
                  variant="outlined"
                  label={'Roles'}
                  helperText="Type 3 letters to search"
                />
              }
            />
          </Grid>

          <Grid size={12}>
            <Box sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography
                sx={{
                  borderBottom: '1px solid',
                  mb: 2,
                  borderColor: 'divider'
                }}
                variant={'subtitle1'}>
                  {'Role Rates'}
              </Typography>

              <Grid mb={2} container alignItems={'center'} spacing={2}>
                <Grid size={4}>
                  <Typography variant={'subtitle2'}>{'HOURLY RATE:'}</Typography>
                </Grid>
                <Grid size={8}>
                  <CustomTextField
                    name="hourly_rate"
                    label="Hourly Rate Amount"
                    placeholder="Enter the hourly rate amount"
                    fullWidth
                    value={formData.hourly_rate}
                    onChange={(e) => handleInputChange("hourly_rate", e.target.value)}
                    disabled={formLoading}
                  />
                </Grid>
              </Grid>

              <Grid mb={2} container alignItems={'center'} spacing={2}>
                <Grid size={4}>
                  <Typography variant={'subtitle2'}>{'PER STREAM RATE:'}</Typography>
                </Grid>
                <Grid size={8}>
                  <CustomTextField
                    name="stream_rate"
                    label="Per Stream Rate Amount"
                    placeholder="Enter the per stream rate amount"
                    fullWidth
                    value={formData.stream_rate}
                    onChange={(e) => handleInputChange("stream_rate", e.target.value)}
                    disabled={formLoading}
                  />
                </Grid>
              </Grid>

              <Grid mb={2} container alignItems={'center'} spacing={2}>
                <Grid size={4}>
                  <Typography variant={'subtitle2'}>{'DAILY RATE:'}</Typography>
                </Grid>
                <Grid size={8}>
                  <CustomTextField
                    name="daily_rate"
                    label="Daily Rate Amount"
                    placeholder="Enter the daily rate amount"
                    fullWidth
                    value={formData.daily_rate}
                    onChange={(e) => handleInputChange("daily_rate", e.target.value)}
                    disabled={formLoading}
                  />
                </Grid>
              </Grid>

              <Grid mb={2} container alignItems={'center'} spacing={2}>
                <Grid size={4}>
                  <Typography variant={'subtitle2'}>{'SALARIED RATE:'}</Typography>
                </Grid>
                <Grid size={8}>
                  <CustomTextField
                    name="salaried_rate"
                    label="Salaried Rate Amount"
                    placeholder="Enter the salaried rate amount"
                    fullWidth
                    value={formData.salaried_rate}
                    onChange={(e) => handleInputChange("salaried_rate", e.target.value)}
                    disabled
                  />
                </Grid>
              </Grid>

              <Grid container alignItems={'center'} spacing={2}>
                <Grid size={4}>
                  <Typography variant={'subtitle2'}>{'FIXED RATE:'}</Typography>
                </Grid>
                <Grid size={8}>
                  <CustomTextField
                    name="fixed_rate"
                    label="Fixed Rate Amount"
                    placeholder="Enter the fixed rate amount"
                    fullWidth
                    value={formData.fixed_rate}
                    onChange={(e) => handleInputChange("fixed_rate", e.target.value)}
                    disabled
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomTextField
              name="currency"
              label="Currency"
              fullWidth
              type={'text'}
              variant="outlined"
              value={formData.currency}
              disabled
              onChange={(e) => handleInputChange("currency", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomTextField
              name="effective_date"
              label="Effective Date"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth
              type="date"
              variant="outlined"
              value={formData.effective_date}
              onChange={(e) => handleInputChange("effective_date", e.target.value)}
            />
          </Grid>  
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("");
            resetForm();
          }}
          sx={{
            color: reduxTheme.primary_colors[400],
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: reduxTheme.primary_colors[400]
          }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateRate}
          disabled={formLoading}
          sx={{ bgcolor: reduxTheme.primary_colors[400] }}
        >
          {formLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}Create Rate Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRateDialog;
